/**
 * Contrato de la capa de datos.
 *
 * Toda la interfaz habla contra este puerto, nunca directamente contra
 * Supabase. Hay dos implementaciones:
 *
 *   - `SupabaseRepository`  — produccion (seccion 13 de la especificacion)
 *   - `DemoRepository`      — datos de ejemplo en el navegador, para poder
 *                             arrancar con `npm run dev` sin backend
 *
 * Esa separacion es tambien la mitigacion del riesgo "cambios en las politicas
 * de las APIs" de la seccion 20: la integracion queda aislada en un modulo
 * sustituible.
 */

import type {
  AdminMetrics,
  AuditLogEntry,
  Badge,
  Competition,
  CompetitionStatus,
  CompetitionTheme,
  Enrollment,
  Exercise,
  ForumPost,
  GenderCategory,
  Language,
  PersonalBest,
  Profile,
  PublicSubmission,
  ReactionKind,
  SubmissionComment,
  PublicProfile,
  RankingPeriod,
  RankingRow,
  RankingScope,
  RejectionReason,
  Report,
  Season,
  Submission,
  SubmissionStatus,
  UserBadge,
  UserRole,
  WeightClass,
} from '@/types/domain';
import type { BonusCode } from '@/config/scoring';

/** Como entro esta cuenta. Se ensena en ajustes, para que sepas cual usas. */
export type AuthProvider = 'EMAIL' | 'GOOGLE';

export interface AuthUser {
  id: string;
  email: string;
  emailVerified: boolean;
  provider: AuthProvider;
}

export interface AuthSession {
  user: AuthUser;
  /** El perfil es null entre el registro y el final del onboarding. */
  profile: Profile | null;
}

export interface SignUpInput {
  email: string;
  password: string;
  acceptedTerms: boolean;
  confirmedAge: boolean;
}

export interface CreateProfileInput {
  alias: string;
  avatarUrl: string | null;
  language: Language;
  country: string;
  city: string;
  birthDate: string | null;
  genderCategory: GenderCategory;
  bodyWeightKg: number;
}

export interface UpdateProfileInput {
  alias?: string;
  avatarUrl?: string | null;
  language?: Language;
  country?: string;
  city?: string;
  bodyWeightKg?: number;
  privacyLevel?: Profile['privacy_level'];
  soundsEnabled?: boolean;
  emailCompetitions?: boolean;
  onboardingCompleted?: boolean;
}

export interface CompetitionFilter {
  status?: CompetitionStatus | 'ACTIVE_OR_SCHEDULED';
  seasonId?: string;
  theme?: CompetitionTheme;
  limit?: number;
}

/**
 * Lo que manda el atleta: el video y poco mas.
 *
 * Ya no declara ejercicio, marca ni lastre. La marca la lee el revisor del
 * video, que es la unica fuente fiable —lo declarado y lo grabado no siempre
 * coincidian, y el desfase acababa en la cola de revision de todos modos—.
 * A cambio, el video tiene que ENSENAR todo lo que se va a puntuar: las
 * repeticiones, el cronometro del aguante y los discos del lastre.
 */
export interface SubmitMarkInput {
  competitionId: string;
  postUrl: string;
  notes: string | null;
}

export interface RankingQuery {
  scope: RankingScope;
  /** Pais o ciudad en scope local; id de competicion en scope COMPETITION. */
  scopeKey?: string;
  seasonId?: string;
  genderCategory?: GenderCategory | 'ALL';
  weightClass?: WeightClass | 'ALL';
  theme?: CompetitionTheme | 'ALL';
  rankLevel?: number | 'ALL';
  period?: RankingPeriod;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface RankingResult {
  rows: RankingRow[];
  total: number;
  page: number;
  pageSize: number;
  /** Fila del usuario que consulta, para fijarla al pie (RF-RANK-07). */
  currentUserRow: RankingRow | null;
  /** Ambito real servido: una ciudad pequena se agrupa al pais (RF-RANK-10). */
  effectiveScope: RankingScope;
  effectiveScopeKey: string | null;
  includesProvisional: boolean;
}

export interface StandingsQuery {
  competitionId: string;
  genderCategory?: GenderCategory | 'ALL';
  weightClass?: WeightClass | 'ALL';
}

export interface StandingsGroup {
  genderCategory: GenderCategory;
  weightClass: WeightClass;
  /** Categorias fusionadas en este grupo, si las hubo (RF-CAT-06). */
  mergedFrom: WeightClass[];
  rows: RankingRow[];
}

export interface ProfileStats {
  competitionsWon: number;
  approvedSubmissions: number;
  /** Quincenas seguidas con al menos un envio, cruzando temporadas. */
  streakFortnights: number;
  globalPosition: number | null;
  localPosition: number | null;
  personalBests: PersonalBest[];
}

export interface AdminSubmissionFilter {
  status?: SubmissionStatus | 'PENDING_ANY';
  competitionId?: string;
  theme?: CompetitionTheme;
  genderCategory?: GenderCategory;
  weightClass?: WeightClass;
  search?: string;
  sortBy?: 'created_at' | 'competition' | 'category' | 'athlete';
  limit?: number;
}

/** Envio con los datos del atleta y de la competicion ya resueltos. */
export interface SubmissionDetail {
  submission: Submission;
  athlete: PublicProfile;
  competition: Competition;
  exercise: Exercise;
}

export interface ScoreSubmissionInput {
  /**
   * Ejercicio que el revisor ve en el video.
   *
   * Hace falta porque el atleta ya no lo declara: en las competiciones que
   * admiten varios movimientos, el envio llega con el primero como marcador y
   * es aqui donde se pone el de verdad.
   */
  correctedExerciseId?: string;
  submissionId: string;
  /** Correcciones del administrador sobre lo declarado (RF-ADM-02). */
  correctedReps?: number | null;
  correctedSeconds?: number | null;
  correctedWeightKg?: number | null;
  bonuses?: BonusCode[];
  manualAdjustment?: number | null;
  manualAdjustmentReason?: string | null;
  adminNote?: string | null;
}

export interface RejectSubmissionInput {
  submissionId: string;
  reason: RejectionReason;
  comment: string;
}

export interface UpsertCompetitionInput extends Partial<Omit<Competition, 'id'>> {
  id?: string;
}

export interface UpsertExerciseInput extends Partial<Omit<Exercise, 'id'>> {
  id?: string;
}

export interface UpsertSeasonInput extends Partial<Omit<Season, 'id'>> {
  id?: string;
}

export interface AdminUserUpdate {
  userId: string;
  role?: UserRole;
  bodyWeightKg?: number;
  weightClass?: WeightClass;
  genderCategory?: GenderCategory;
  suspendedUntil?: string | null;
  banned?: boolean;
  reason: string;
}

export interface WeightClassThresholds {
  M: { code: WeightClass; maxKg: number | null }[];
  F: { code: WeightClass; maxKg: number | null }[];
  minParticipants: number;
}

/** Error de dominio con una clave de traduccion, no un texto suelto. */
export class RepositoryError extends Error {
  constructor(
    /** Clave dentro de `errors.*` de los ficheros de idioma. */
    public readonly code: string,
    /** Valores de interpolacion del mensaje. */
    public readonly params: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'RepositoryError';
  }
}

/** Mensaje del formulario de contacto de "Quien soy". */
export interface ContactMessageInput {
  name: string;
  /** Solo para poder responder: no se muestra en ningun sitio. */
  email: string;
  message: string;
}

export interface Repository {
  readonly isDemo: boolean;

  // --- Autenticacion (5.1) ---------------------------------------------------
  getSession(): Promise<AuthSession | null>;
  onAuthChange(listener: (session: AuthSession | null) => void): () => void;
  signUpWithEmail(input: SignUpInput): Promise<AuthSession>;
  signInWithEmail(email: string, password: string): Promise<AuthSession>;
  /**
   * Entra con Google. Devuelve la sesion si la identificacion se resolvio aqui
   * mismo, y `null` cuando el navegador se esta yendo al proveedor: en ese caso
   * la pantalla no debe navegar a ningun sitio, porque ya no va a estar.
   */
  signInWithGoogle(redirectTo: string): Promise<AuthSession | null>;
  signOut(): Promise<void>;
  signOutEverywhere(): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  resendVerificationEmail(): Promise<void>;
  /** Solo en modo demo: entra como una de las cuentas de ejemplo. */
  demoSignInAs?(userId: string): Promise<AuthSession>;
  demoAccounts?(): { id: string; alias: string; role: UserRole }[];

  // --- Perfil (5.3, 9.4) -----------------------------------------------------
  createProfile(input: CreateProfileInput): Promise<Profile>;
  getMyProfile(): Promise<Profile | null>;
  updateMyProfile(input: UpdateProfileInput): Promise<Profile>;
  isAliasAvailable(alias: string): Promise<boolean>;
  getPublicProfile(alias: string): Promise<PublicProfile | null>;
  getProfileStats(userId: string): Promise<ProfileStats>;
  searchProfiles(query: string, limit?: number): Promise<PublicProfile[]>;
  exportMyData(): Promise<Record<string, unknown>>;
  deleteMyAccount(): Promise<void>;

  // --- Temporadas (6.1) ------------------------------------------------------
  getCurrentSeason(): Promise<Season | null>;
  listSeasons(): Promise<Season[]>;

  // --- Competiciones (6.2) ---------------------------------------------------
  listCompetitions(filter?: CompetitionFilter): Promise<Competition[]>;
  getCompetition(idOrSlug: string): Promise<Competition | null>;
  enroll(competitionId: string): Promise<Enrollment>;
  listMyEnrollments(): Promise<Enrollment[]>;
  getStandings(query: StandingsQuery): Promise<StandingsGroup[]>;

  // --- Ejercicios (8.2) ------------------------------------------------------
  listExercises(): Promise<Exercise[]>;

  // --- Envios (7) ------------------------------------------------------------
  submitMark(input: SubmitMarkInput): Promise<Submission>;
  listMySubmissions(): Promise<Submission[]>;
  getMySubmissionFor(competitionId: string): Promise<Submission | null>;
  deleteSubmission(submissionId: string): Promise<void>;

  // --- Rankings (9.3) --------------------------------------------------------
  getRanking(query: RankingQuery): Promise<RankingResult>;

  // --- Insignias (9.2) -------------------------------------------------------
  listBadges(): Promise<Badge[]>;
  listUserBadges(userId: string): Promise<UserBadge[]>;
  setBadgeFeatured(userBadgeId: string, featured: boolean): Promise<void>;

  // --- Social (9.4) ----------------------------------------------------------
  follow(userId: string): Promise<void>;
  unfollow(userId: string): Promise<void>;
  isFollowing(userId: string): Promise<boolean>;
  createReport(
    targetType: Report['target_type'],
    targetId: string,
    reason: string,
  ): Promise<void>;

  // --- Reacciones y comentarios (social) -------------------------------------
  // Solo sobre marcas APROBADAS: lo que no ha pasado revision no es publico.
  listCompetitionSubmissions(competitionId: string): Promise<PublicSubmission[]>;
  getPublicSubmission(submissionId: string): Promise<PublicSubmission | null>;
  /** `null` quita la reaccion propia; cualquier otra la sustituye. */
  setReaction(submissionId: string, kind: ReactionKind | null): Promise<void>;
  listComments(submissionId: string): Promise<SubmissionComment[]>;
  addComment(submissionId: string, body: string): Promise<SubmissionComment>;
  deleteComment(commentId: string): Promise<void>;

  // --- Foro de la comunidad --------------------------------------------------
  /**
   * El tablon entero —mensajes y respuestas— de lo mas nuevo a lo mas viejo.
   * Vienen en una sola lista, con `parent_id` para saber quien cuelga de
   * quien: agruparlos es cosa de la pantalla (src/lib/social.ts).
   */
  listForumPosts(): Promise<ForumPost[]>;
  /** Sin `parentId` es un mensaje nuevo del tablon; con el, una respuesta. */
  addForumPost(body: string, parentId?: string | null): Promise<ForumPost>;
  /** Solo el autor borra el suyo. Moderacion oculta, que no es lo mismo. */
  deleteForumPost(postId: string): Promise<void>;

  // --- Contacto ("Quien soy") ------------------------------------------------
  /** Abierto a cualquiera, con o sin sesion. */
  sendContactMessage(input: ContactMessageInput): Promise<void>;

  // --- Integraciones sociales (7.3, fase 2) ---------------------------------
  connectSocialAccount(platform: 'INSTAGRAM' | 'TIKTOK', redirectTo: string): Promise<void>;
  disconnectSocialAccount(platform: 'INSTAGRAM' | 'TIKTOK'): Promise<void>;
  listAutoDetectedDrafts(): Promise<Submission[]>;

  // --- Administracion (10) ---------------------------------------------------
  adminListSubmissions(filter?: AdminSubmissionFilter): Promise<SubmissionDetail[]>;
  adminGetSubmission(submissionId: string): Promise<SubmissionDetail | null>;
  adminScoreSubmission(input: ScoreSubmissionInput): Promise<Submission>;
  adminRejectSubmission(input: RejectSubmissionInput): Promise<Submission>;
  adminFlagForSecondOpinion(submissionId: string, note: string): Promise<Submission>;
  adminUpsertCompetition(input: UpsertCompetitionInput): Promise<Competition>;
  /**
   * Solo si nadie ha enviado su prueba: con un envio, en cualquier estado,
   * falla con `competitionHasSubmissions`. Las inscripciones sin envio se van
   * con ella.
   */
  adminDeleteCompetition(competitionId: string): Promise<void>;
  adminPublishResults(competitionId: string): Promise<void>;
  adminUpsertExercise(input: UpsertExerciseInput): Promise<Exercise>;
  adminUpsertSeason(input: UpsertSeasonInput): Promise<Season>;
  adminCloseSeason(seasonId: string): Promise<void>;
  adminListUsers(search?: string): Promise<Profile[]>;
  adminUpdateUser(input: AdminUserUpdate): Promise<Profile>;
  adminGrantBadge(userId: string, badgeId: string): Promise<void>;
  adminRevokeBadge(userBadgeId: string): Promise<void>;
  adminGetWeightClasses(): Promise<WeightClassThresholds>;
  adminUpdateWeightClasses(input: WeightClassThresholds): Promise<void>;
  adminGetMetrics(): Promise<AdminMetrics>;
  adminListAuditLog(limit?: number): Promise<AuditLogEntry[]>;
  /**
   * Traduccion corta entre español e ingles para el panel (solo moderacion).
   * Crear una competicion exige escribirlo todo en los dos idiomas; esto
   * rellena el lado que falta.
   */
  adminTranslate(text: string, from: Language, to: Language): Promise<string>;
  adminListReports(): Promise<Report[]>;
  /** Comentarios por id, para poder leer lo denunciado antes de decidir. */
  adminGetComments(ids: string[]): Promise<SubmissionComment[]>;
  /** Ocultar no borra: el comentario deja de verse pero queda para revisarlo. */
  adminSetCommentHidden(commentId: string, hidden: boolean): Promise<void>;
  /**
   * Quita una marca de la galeria publica sin tocar su puntuacion. Para que
   * deje de puntuar hay que rechazarla, que es otra cosa.
   */
  adminSetSubmissionHidden(submissionId: string, hidden: boolean): Promise<void>;
  /** Mensajes del foro por id, para leer lo denunciado antes de decidir. */
  adminGetForumPosts(ids: string[]): Promise<ForumPost[]>;
  adminSetForumPostHidden(postId: string, hidden: boolean): Promise<void>;
  adminResolveReport(reportId: string): Promise<void>;
}
